// Sevkiyat Aşamaları
export type ShipmentStatus = 
  | 'heading-to-loading'          // Yükleme noktasına gidiyor
  | 'arrived-at-loading'          // Yükleme noktasına ulaştı
  | 'waiting-for-loading'         // Yükleme bekliyor (rampa atanması bekleniyor)
  | 'loading-in-progress'         // Yükleme yapılıyor
  | 'loading-completed'           // Yükleme tamamlandı, çıkış bekliyor
  | 'heading-to-delivery'         // Teslimat noktasına gidiyor
  | 'arrived-at-delivery'         // Teslimat noktasına vardı
  | 'waiting-for-unloading'       // Boşaltma bekliyor
  | 'unloading-in-progress'       // Boşaltma gerçekleşiyor
  | 'unloading-completed'         // Boşaltma tamamlandı
  | 'delivery-completed';         // Teslimat tamamlandı

export interface ShipmentStatusConfig {
  label: string;
  color: string;
  bgColor: string;
  description: string;
  requiresDocument?: boolean;
  nextStatus?: ShipmentStatus;
  allowedRoles?: string[];
}

export const SHIPMENT_STATUS_CONFIG: Record<ShipmentStatus, ShipmentStatusConfig> = {
  'heading-to-loading': {
    label: 'Yükleme Noktasına Gidiyor',
    color: '#1e40af',
    bgColor: '#dbeafe',
    description: 'Araç yükleme noktasına doğru yola çıktı',
    nextStatus: 'arrived-at-loading',
    allowedRoles: ['driver'],
  },
  'arrived-at-loading': {
    label: 'Yükleme Noktasına Ulaştı',
    color: '#7c3aed',
    bgColor: '#ede9fe',
    description: 'Araç yükleme noktasına vardı, rampa ataması bekleniyor',
    nextStatus: 'waiting-for-loading',
    allowedRoles: ['driver'],
  },
  'waiting-for-loading': {
    label: 'Yükleme Bekliyor',
    color: '#ea580c',
    bgColor: '#ffedd5',
    description: 'Rampa atandı, yükleme başlaması bekleniyor',
    nextStatus: 'loading-in-progress',
    allowedRoles: ['driver', 'warehouse'],
  },
  'loading-in-progress': {
    label: 'Yükleme Yapılıyor',
    color: '#0891b2',
    bgColor: '#cffafe',
    description: 'Yükleme işlemi devam ediyor',
    nextStatus: 'loading-completed',
    allowedRoles: ['driver', 'warehouse'],
  },
  'loading-completed': {
    label: 'Yükleme Tamamlandı',
    color: '#16a34a',
    bgColor: '#dcfce7',
    description: 'Yükleme tamamlandı, çıkış evrakı bekleniyor',
    requiresDocument: true,
    nextStatus: 'heading-to-delivery',
    allowedRoles: ['driver'],
  },
  'heading-to-delivery': {
    label: 'Teslimat Noktasına Gidiyor',
    color: '#1e40af',
    bgColor: '#dbeafe',
    description: 'Araç teslimat noktasına doğru yola çıktı',
    nextStatus: 'arrived-at-delivery',
    allowedRoles: ['driver'],
  },
  'arrived-at-delivery': {
    label: 'Teslimat Noktasına Vardı',
    color: '#7c3aed',
    bgColor: '#ede9fe',
    description: 'Araç teslimat noktasına vardı',
    nextStatus: 'waiting-for-unloading',
    allowedRoles: ['driver'],
  },
  'waiting-for-unloading': {
    label: 'Boşaltma Bekliyor',
    color: '#ea580c',
    bgColor: '#ffedd5',
    description: 'Boşaltma başlaması bekleniyor',
    nextStatus: 'unloading-in-progress',
    allowedRoles: ['driver', 'warehouse'],
  },
  'unloading-in-progress': {
    label: 'Boşaltma Gerçekleşiyor',
    color: '#0891b2',
    bgColor: '#cffafe',
    description: 'Boşaltma işlemi devam ediyor',
    nextStatus: 'unloading-completed',
    allowedRoles: ['driver', 'warehouse'],
  },
  'unloading-completed': {
    label: 'Boşaltma Tamamlandı',
    color: '#16a34a',
    bgColor: '#dcfce7',
    description: 'Boşaltma tamamlandı, teslim evrakı bekleniyor',
    requiresDocument: true,
    nextStatus: 'delivery-completed',
    allowedRoles: ['driver'],
  },
  'delivery-completed': {
    label: 'Teslimat Tamamlandı',
    color: '#059669',
    bgColor: '#d1fae5',
    description: 'Sevkiyat başarıyla tamamlandı',
    allowedRoles: [],
  },
};

export interface ShipmentStatusUpdate {
  id: string;
  shipmentId: string;
  status: ShipmentStatus;
  timestamp: string;
  location?: {
    lat: number;
    lng: number;
    address?: string;
  };
  notes?: string;
  updatedBy: string;
  updatedByRole: string;
}

export interface ShipmentDocument {
  id: string;
  shipmentId: string;
  type: 'loading' | 'delivery';
  fileUrl: string;
  fileName: string;
  uploadedAt: string;
  uploadedBy: string;
  status: ShipmentStatus; // Hangi aşamada yüklendi
}

export interface RampAssignment {
  id: string;
  shipmentId: string;
  rampId: string;
  rampName: string;
  assignedAt: string;
  assignedBy: string;
  estimatedArrival?: string;
  actualArrival?: string;
  status: 'assigned' | 'in-use' | 'completed';
}

export interface Shipment {
  id: string;
  sevkKodu: string;
  driverName: string;
  driverId: string;
  vehiclePlate: string;
  vehicleId: string;
  
  // Yükleme Bilgileri
  loadingPoint: string;
  loadingAddress: string;
  loadingCity: string;
  loadingWarehouseId?: string;
  loadingContact?: string;
  plannedLoadingTime?: string;
  
  // Teslimat Bilgileri
  deliveryPoint: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryWarehouseId?: string;
  deliveryContact?: string;
  plannedDeliveryTime?: string;
  
  // Yük Bilgileri
  cargoType: string;
  cargoWeight: number;
  cargoVolume: number;
  
  // Durum Bilgileri
  currentStatus: ShipmentStatus;
  statusHistory: ShipmentStatusUpdate[];
  
  // Evraklar
  documents: ShipmentDocument[];
  
  // Rampa Ataması
  rampAssignment?: RampAssignment;
  
  // Konum Bilgisi
  currentLocation?: {
    lat: number;
    lng: number;
    lastUpdated: string;
  };
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ShipmentNotification {
  id: string;
  type: 'status-update' | 'document-upload' | 'ramp-assignment' | 'proximity-alert';
  shipmentId: string;
  title: string;
  message: string;
  recipients: string[]; // User IDs or roles
  timestamp: string;
  read: boolean;
  data?: any;
}
